/**
 * Ручные цены КП — МСК Газобетон.
 *
 * Менеджер договаривается с клиентом по телефону и меняет цену прямо в готовом
 * сообщении: цену материала за единицу, цену одного рейса, цену разгрузки.
 * Здесь живёт только это — разбор введённого значения, ключ строки и наложение
 * переопределений поверх уже посчитанных строк КП.
 *
 * Своего расчёта модуль не делает и делать не должен: количества, объём, число
 * рейсов и состав строк приходят готовыми из buildGazCommercialProposalBlocks,
 * итог к оплате округляет существующий gazCashRounding.
 *
 * Ручная цена — это цена заказа, а не украшение экрана: менеджер согласовал её с
 * клиентом, и в сделку должна уйти она же. Поэтому переопределения читает не только
 * блок КП, но и сборка товарных строк Битрикса (Offer.js), через общий
 * resolveGazRowManualPrice — ключ строки считается одними правилами с обеих сторон.
 * Раньше цены дальше экрана не шли, и сделка молча создавалась по цене расчёта.
 *
 * В базе переопределения по-прежнему не сохраняются — это состояние страницы. И
 * исходные цены расчёта остаются нетронутыми: по ним по-прежнему сравниваются
 * производства и выбирается минимальный завод.
 */

/** Разделы КП, цены которых правятся руками. */
export const GAZ_PRICE_GROUP_MATERIAL = 'material';
export const GAZ_PRICE_GROUP_DELIVERY = 'delivery';
export const GAZ_PRICE_GROUP_UNLOADING = 'unloading';

/** Единица строки разгрузки: в расчёте она приходит услугой, а не рейсом. */
export const GAZ_UNLOADING_UNIT = 'услуга';

/** Та же точность, что и у сумм в блоках КП. */
export const roundGazPrice = (value) => Math.round((Number(value) || 0) * 1000) / 1000;

/**
 * Ключ строки КП. Собран из того же, по чему строки склеиваются в одну позицию
 * (раздел, название, единица, цена расчёта), поэтому пересчёт с теми же данными
 * даёт тот же ключ, а изменившийся расчёт — другой, и старая правка к нему не
 * прилипает.
 */
export const gazPriceLineId = (group, name, unit, basePrice) => (
  `${group}|${String(name ?? '')}|${String(unit ?? '')}|${roundGazPrice(basePrice)}`
);

/**
 * Разбор введённой цены.
 *
 * Годится только конечное число больше нуля. Пустое поле, ноль, минус,
 * бесконечность, буквы и прочий мусор — это «применить нельзя»: молча
 * подставленный ноль ушёл бы клиенту ценой, а нечисло сломало бы итог —
 * вместо суммы встало бы «NaN ₽».
 *
 * @returns {{ isValid: boolean, value: number|null }}
 */
export const parseGazManualPrice = (raw) => {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw > 0
      ? { isValid: true, value: roundGazPrice(raw) }
      : { isValid: false, value: null };
  }

  // Разряды набирают пробелами, дробную часть — запятой: и то и другое допустимо.
  const text = String(raw ?? '').replace(/[\s ]/g, '').replace(',', '.');
  if (!text || !/^\d+(\.\d+)?$/.test(text)) {
    return { isValid: false, value: null };
  }

  const value = Number(text);
  // Округление до той же точности не должно превращать цену в ноль: «0,0001 ₽»
  // это опечатка, а не цена.
  if (!Number.isFinite(value) || roundGazPrice(value) <= 0) {
    return { isValid: false, value: null };
  }

  return { isValid: true, value: roundGazPrice(value) };
};

/**
 * Состояние набранных, но ещё не сохранённых цен.
 *
 * Черновик — это то, что менеджер набрал в поле; применённая цена — то, что уже
 * стоит в расчёте и в сообщении клиенту. Отсюда видно, что негодно, что менеджер
 * действительно изменил и можно ли сохранять.
 *
 * @param {object} drafts        — сырые строки полей по ключам строк КП
 * @param {object} appliedPrices — применённая цена по тем же ключам
 * @returns {{ values: object, invalidIds: string[], changedIds: string[],
 *             hasInvalid: boolean, isDirty: boolean, canSave: boolean }}
 */
export const resolveGazPriceDrafts = (drafts, appliedPrices) => {
  const values = {};
  const invalidIds = [];
  const changedIds = [];

  for (const [id, raw] of Object.entries(drafts || {})) {
    const parsed = parseGazManualPrice(raw);
    if (!parsed.isValid) {
      invalidIds.push(id);
      continue;
    }
    values[id] = parsed.value;
    const applied = Number(appliedPrices ? appliedPrices[id] : NaN);
    if (!Number.isFinite(applied) || roundGazPrice(applied) !== parsed.value) {
      changedIds.push(id);
    }
  }

  return {
    values,
    invalidIds,
    changedIds,
    hasInvalid: invalidIds.length > 0,
    // Недобранное значение — тоже несохранённая правка: молча терять её нельзя.
    isDirty: invalidIds.length > 0 || changedIds.length > 0,
    canSave: invalidIds.length === 0 && changedIds.length > 0,
  };
};

/** Оставляет только пригодные переопределения: остальное к строкам не применяется. */
export const normalizeGazPriceOverrides = (overrides) => {
  if (!overrides || typeof overrides !== 'object') {
    return {};
  }

  const result = {};
  for (const [id, value] of Object.entries(overrides)) {
    const parsed = parseGazManualPrice(value);
    if (parsed.isValid) {
      result[id] = parsed.value;
    }
  }
  return result;
};

/**
 * Строка КП с наложенной ручной ценой.
 *
 * Без переопределения строка возвращается как есть — вместе с суммой, которую
 * посчитал расчёт. Пересчитывается сумма только там, где цену действительно
 * поменяли: иначе округления расчёта затирались бы умножением «цена × количество».
 *
 * @param {object} row       — склеенная строка КП: name, unit, quantity, pricePerOne, sum
 * @param {string} group     — раздел КП
 * @param {object} overrides — ручные цены по ключам строк
 */
export const applyGazManualPrice = (row, group, overrides) => {
  const basePrice = roundGazPrice(row.pricePerOne);
  const id = gazPriceLineId(group, row.name, row.unit, basePrice);
  const override = overrides ? overrides[id] : undefined;
  const parsed = parseGazManualPrice(override);
  const isManual = parsed.isValid && parsed.value !== basePrice;
  const price = isManual ? parsed.value : basePrice;

  return {
    ...row,
    id,
    group,
    basePrice,
    baseSum: Number(row.sum) || 0,
    pricePerOne: price,
    sum: isManual ? roundGazPrice(price * (Number(row.quantity) || 0)) : (Number(row.sum) || 0),
    isManual,
  };
};

/**
 * Отпечаток расчёта: строки, их количества и цены расчёта.
 *
 * По нему видно, что изменилось само предложение — производство, состав или
 * количество товаров, адрес (через цену доставки), вариант заказа, способ
 * разгрузки. Ручные цены такого расчёта уже не касаются и сбрасываются.
 */
export const gazPriceLinesKey = (lines) => (Array.isArray(lines) ? lines : [])
  .map((line) => `${line.id}#${roundGazPrice(line.quantity)}`)
  .join(';');

export default applyGazManualPrice;
